import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Save, History, FileDown, Database } from "lucide-react";
import MarginAnalysis from "@/components/MarginAnalysis";
import ProjectInfoForm from "@/components/ProjectInfoForm";
import TxdotBidHelper from "@/components/TxdotBidHelper";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SectionInput = ({ title, value, onChange, testIdPrefix }) => {
  return (
    <Card className="millmetrix-card p-6" data-testid={`${testIdPrefix}-card`}>
      <div className="section-header">
        <h3 className="text-xl font-semibold tracking-tight uppercase text-muted-foreground">
          {title}
        </h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor={`${testIdPrefix}-lf`} className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Linear Feet
          </Label>
          <Input
            id={`${testIdPrefix}-lf`}
            data-testid={`${testIdPrefix}-lf-input`}
            type="number"
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            className="millmetrix-input mt-2 text-2xl font-mono font-semibold h-14"
            placeholder="0"
          />
        </div>
      </div>
    </Card>
  );
};

const Calculator = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { division } = useDivision();
  
  const [asphaltShoulder, setAsphaltShoulder] = useState(0);
  const [asphaltCenterline, setAsphaltCenterline] = useState(0);
  const [concreteShoulder, setConcreteShoulder] = useState(0);
  const [concreteCenterline, setConcreteCenterline] = useState(0);
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [priceAdjustment, setPriceAdjustment] = useState(0);
  const [showTxdotHelper, setShowTxdotHelper] = useState(false);
  
  const [projectInfo, setProjectInfo] = useState({
    county: '',
    highway: '',
    control_number: '',
    letting_date: ''
  });

  // Handler to apply TXDOT quantities from the helper
  const handleApplyTxdotQuantities = (quantities) => {
    setAsphaltShoulder(quantities.asphalt_shoulder);
    setAsphaltCenterline(quantities.asphalt_centerline);
    setConcreteShoulder(quantities.concrete_shoulder);
    setConcreteCenterline(quantities.concrete_centerline);
  };

  useEffect(() => {
    calculateBid();
  }, [
    asphaltShoulder,
    asphaltCenterline,
    concreteShoulder,
    concreteCenterline
  ]);

  const calculateBid = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate`, {
        asphalt_shoulder: {
          linear_feet: asphaltShoulder,
          include_tc_cleanup: false
        },
        asphalt_centerline: {
          linear_feet: asphaltCenterline,
          include_tc_cleanup: false
        },
        concrete_shoulder: {
          linear_feet: concreteShoulder,
          include_tc_cleanup: false
        },
        concrete_centerline: {
          linear_feet: concreteCenterline,
          include_tc_cleanup: false
        }
      });
      setResult(response.data);
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error("Failed to calculate bid");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const saveBid = async () => {
    // Validate project info
    if (!projectInfo.county || !projectInfo.highway || !projectInfo.control_number) {
      toast.error("Please fill in all project information fields");
      return;
    }

    if (!result || result.total_linear_feet === 0) {
      toast.error("Please calculate a bid before saving");
      return;
    }

    try {
      const response = await axios.post(`${API}/bids/save`, {
        project_info: projectInfo,
        calculation: {
          asphalt_shoulder: {
            linear_feet: asphaltShoulder,
            include_tc_cleanup: false
          },
          asphalt_centerline: {
            linear_feet: asphaltCenterline,
            include_tc_cleanup: false
          },
          concrete_shoulder: {
            linear_feet: concreteShoulder,
            include_tc_cleanup: false
          },
          concrete_centerline: {
            linear_feet: concreteCenterline,
            include_tc_cleanup: false
          }
        },
        selected_price_adjustment: priceAdjustment
      }, {
        headers: getAuthHeaders(),
        params: { division }
      });

      toast.success("Bid saved successfully!");
      
      // Create a simple link to download the PDF
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = `${API}/bids/${response.data.id}/pdf`;
        link.download = `bid_${response.data.id.substring(0, 8)}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("PDF download started - check your Downloads folder!");
      }, 300);
      
    } catch (error) {
      console.error("Error saving bid:", error);
      toast.error("Failed to save bid");
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay"></div>
      
      {/* TXDOT Bid Helper Dialog */}
      <TxdotBidHelper 
        isOpen={showTxdotHelper}
        onClose={() => setShowTxdotHelper(false)}
        onApplyQuantities={handleApplyTxdotQuantities}
      />
      
      <div className="relative z-10 ml-64 container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2" data-testid="app-title">
              <span className="text-[#f97316]">Mill</span>Metrix
            </h1>
            <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
              Precision Bid Calculator for Milled Rumble Strips
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowTxdotHelper(true)}
              className="millmetrix-button bg-[#8b5cf6] hover:bg-[#7c3aed] text-white"
              data-testid="txdot-lookup-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              TXDOT Lookup
            </Button>
            <Button
              onClick={() => navigate('/history')}
              className="millmetrix-button bg-[#0ea5e9] hover:bg-[#0284c7] text-white"
              data-testid="view-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Bid History
            </Button>
          </div>
        </div>

        {/* Project Info */}
        <div className="mb-6">
          <ProjectInfoForm projectInfo={projectInfo} onChange={setProjectInfo} />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <SectionInput
              title="Asphalt Shoulder"
              value={asphaltShoulder}
              onChange={setAsphaltShoulder}
              testIdPrefix="asphalt-shoulder"
            />
            
            <SectionInput
              title="Asphalt Centerline"
              value={asphaltCenterline}
              onChange={setAsphaltCenterline}
              testIdPrefix="asphalt-centerline"
            />
            
            <SectionInput
              title="Concrete Shoulder"
              value={concreteShoulder}
              onChange={setConcreteShoulder}
              testIdPrefix="concrete-shoulder"
            />
            
            <SectionInput
              title="Concrete Centerline"
              value={concreteCenterline}
              onChange={setConcreteCenterline}
              testIdPrefix="concrete-centerline"
            />
          </div>

          {/* Margin Analysis Section - Full Width */}
          {result && result.total_linear_feet > 0 && (
            <div className="lg:col-span-12">
              <MarginAnalysis 
                result={result} 
                selectedAdjustment={priceAdjustment}
                onSelectAdjustment={setPriceAdjustment}
              />
            </div>
          )}

          {/* Results Section */}
          <div className="lg:col-span-4 flex flex-col gap-4 lg:sticky lg:top-6 lg:self-start">
            <Card className="result-card p-6 backdrop-blur-xl" data-testid="live-bid-engine">
              <h2 className="text-3xl font-bold tracking-tight mb-6 flex items-center gap-2 text-[#f8fafc]">
                <span className="w-2 h-8 bg-[#f97316] rounded"></span>
                Live Bid Engine
              </h2>
              
              {result && result.total_linear_feet > 0 ? (
                <div className="space-y-6">
                  {/* Production Metrics */}
                  <div className="metric-card">
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                      Production
                    </div>
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-sm text-[#94a3b8]">Total Linear Feet</span>
                      <span className="text-2xl font-mono font-semibold tabular-nums text-[#f8fafc]" data-testid="total-lf">
                        {formatNumber(result.total_linear_feet, 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-[#94a3b8]">Production Days</span>
                      <span className="text-2xl font-mono font-semibold tabular-nums text-[#f8fafc]" data-testid="production-days">
                        {formatNumber(result.production_days, 1)}
                      </span>
                    </div>
                  </div>

                  {/* Bid Prices - Both Options */}
                  <div className="space-y-3">
                    <div className="metric-card bg-[#1e293b] border border-[rgba(14,165,233,0.3)]">
                      <div className="text-xs font-bold uppercase tracking-widest text-[#94a3b8] mb-2">
                        Milling Only Bid Price
                      </div>
                      <div className="text-3xl font-mono font-bold tracking-tight text-[#0ea5e9] tabular-nums" data-testid="total-price-milling">
                        {formatCurrency(result.total_extended_price)}
                      </div>
                      <div className="text-xs text-[#94a3b8] mt-1">
                        Select this when bid is awarded
                      </div>
                    </div>

                    <div className="metric-card bg-[#1e293b] border border-[rgba(249,115,22,0.3)]">
                      <div className="text-xs font-bold uppercase tracking-widest text-[#94a3b8] mb-2">
                        With Traffic Control & Cleanup
                      </div>
                      <div className="text-3xl font-mono font-bold tracking-tight text-[#f97316] tabular-nums" data-testid="total-price-with-tc">
                        {formatCurrency(result.total_extended_price_with_tc)}
                      </div>
                      <div className="text-xs text-[#94a3b8] mt-1">
                        Select this when bid is awarded
                      </div>
                    </div>
                  </div>

                  {/* Financial Metrics */}
                  <div className="space-y-3">
                    <div className="metric-card">
                      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                        Cost Estimate
                      </div>
                      <div className="text-2xl font-mono font-bold tracking-tight text-[#94a3b8] tabular-nums" data-testid="total-cost">
                        {formatCurrency(result.total_cost)}
                      </div>
                    </div>

                    <div className="metric-card profit-glow">
                      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                        Projected Profit
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#94a3b8]">Milling Only:</span>
                          <span className="text-xl font-mono font-bold tracking-tight text-[#10b981] tabular-nums" data-testid="total-profit">
                            {formatCurrency(result.total_profit)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#94a3b8]">With TC&C:</span>
                          <span className="text-xl font-mono font-bold tracking-tight text-[#0ea5e9] tabular-nums" data-testid="total-profit-tc">
                            {formatCurrency(result.total_profit_with_tc || (result.total_extended_price_with_tc - result.total_cost))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Margin & Daily Profit */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="metric-card">
                      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                        Margin
                      </div>
                      <div className="text-3xl font-mono font-bold tracking-tight tabular-nums text-[#f8fafc]" data-testid="margin-percent">
                        {formatNumber(result.overall_margin_percent, 1)}%
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                        Daily Profit
                      </div>
                      <div className="text-xl font-mono font-bold tracking-tight text-[#10b981] tabular-nums" data-testid="daily-profit">
                        {formatCurrency(result.daily_profit)}
                      </div>
                    </div>
                  </div>

                  {/* Current Selection Display */}
                  {priceAdjustment !== 0 && (
                    <div className="bg-[#1e293b] border border-[rgba(249,115,22,0.3)] p-4 rounded-md">
                      <div className="text-xs font-bold uppercase tracking-widest text-[#f97316] mb-3">
                        Selected Price Adjustment
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-[#94a3b8]">Adjustment</span>
                          <span className="text-2xl font-mono font-semibold tabular-nums text-[#f97316]">
                            {priceAdjustment > 0 ? '+' : ''}{formatCurrency(priceAdjustment)}/LF
                          </span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-[#94a3b8]">New Revenue</span>
                          <span className="text-lg font-mono font-semibold tabular-nums text-[#0ea5e9]">
                            {formatCurrency(result.total_extended_price + (priceAdjustment * result.total_linear_feet))}
                          </span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-[#94a3b8]">New Profit</span>
                          <span className="text-lg font-mono font-semibold tabular-nums text-[#10b981]">
                            {formatCurrency(result.total_profit + (priceAdjustment * result.total_linear_feet))}
                          </span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-[#94a3b8]">New Margin</span>
                          <span className="text-lg font-mono font-semibold tabular-nums text-[#f8fafc]">
                            {formatNumber(((result.total_profit + (priceAdjustment * result.total_linear_feet)) / (result.total_extended_price + (priceAdjustment * result.total_linear_feet)) * 100), 1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Save Bid Button */}
                  <div className="pt-4 border-t border-[rgba(255,255,255,0.1)]">
                    <Button
                      onClick={saveBid}
                      className="millmetrix-button w-full bg-[#10b981] hover:bg-[#059669] text-white py-6 text-lg"
                      data-testid="save-bid-btn"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      Save Bid and Download PDF
                    </Button>
                    <p className="text-xs text-[#94a3b8] text-center mt-2">
                      Saves to database and downloads PDF to your Downloads folder
                    </p>
                  </div>

                  {/* Section Breakdown */}
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                      Per-Section Pricing
                    </div>
                    <div className="space-y-2">
                      {result.sections.map((section, idx) => (
                        <div key={idx} className="bg-[#1e293b] p-3 rounded-md" data-testid={`section-result-${idx}`}>
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-semibold">{section.section_name}</span>
                            <span className="text-xs text-[#94a3b8]">
                              {formatNumber(section.linear_feet, 0)} LF
                            </span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-xs text-[#94a3b8]">Price/LF</span>
                            <span className="text-lg font-mono font-semibold tabular-nums">
                              {formatCurrency(section.price_per_lf + priceAdjustment)}
                            </span>
                          </div>
                          <div className="flex justify-between items-baseline mt-1">
                            <span className="text-xs text-[#94a3b8]">Extended</span>
                            <span className="text-sm font-mono tabular-nums">
                              {formatCurrency((section.price_per_lf + priceAdjustment) * section.linear_feet)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12" data-testid="empty-state">
                  <div className="text-[#94a3b8] text-sm">
                    Enter linear footage to calculate bid pricing
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
